// AnimalListItemVerticalEditable.tsx (Label-Value-Pair layout with old and editable new value)
import React from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TableHead,
  TableBody,
  Paper
} from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEndorsmentFormData,
  selectPolicyDetails,
  updateEndorsementFormData
} from 'src/features/rfqDataSlice';
import moment from 'moment';

function AnimalListItemVerticalEditable({ sumInsuredData, groupDetails, productId }) {
  const dispatch = useDispatch();
  const endorsementFormData = useSelector(selectEndorsmentFormData);
  const policyDetails = useSelector(selectPolicyDetails);

  const sectionId = sumInsuredData?.id;
  const groupId = groupDetails?.id;

  const selectedHeads = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredSelectedHeads || [];
  const tableListDatas = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredEntry || [];

  const oldRow = policyDetails?.policySumInsuredSections.filter(item => item.id == sectionId)?.[0] || {}; // original values
  const newRow = tableListDatas?.[0] || {}; // current editable values

  const handleChange = (attr, value) => {
    const updatedRow = { ...newRow, [attr]: value, savedStatus: false, actionType: 'update' };
    const updatedEntryList = [updatedRow];

    const updatedGroup = {
      ...endorsementFormData.sumInsuredSectionData[sectionId].groups[groupId],
      sumInsuredEntry: updatedEntryList
    };

    const updatedSection = {
      ...endorsementFormData.sumInsuredSectionData[sectionId],
      groups: {
        ...endorsementFormData.sumInsuredSectionData[sectionId].groups,
        [groupId]: updatedGroup
      }
    };

    dispatch(
      updateEndorsementFormData({
        sumInsuredSectionData: {
          ...endorsementFormData.sumInsuredSectionData,
          [sectionId]: updatedSection
        }
      })
    );
  };

  const renderEditableInput = (attr, value) => {
    if (['dob', 'dateOfCoverage'].includes(attr)) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={['year', 'month', 'day']}
            inputFormat="dd-MM-yyyy"
            value={value ? moment(value, "DD-MM-YYYY").toDate() : null}
            onChange={(val) => {
              const formattedDate = val ? moment(val).format("DD-MM-YYYY") : null;
              handleChange(attr, formattedDate);
            }}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField {...params} size="small" fullWidth />
            )}
          />
        </LocalizationProvider>
      );
    }

    if (attr === 'gender') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Gender</InputLabel>
          <Select
            label="Gender"
            value={value || ''}
            onChange={(e) => handleChange(attr, e.target.value)}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        size="small"
        fullWidth
        type={['age', 'sumInsured', 'premium'].includes(attr) ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => handleChange(attr, e.target.value)}
      />
    );
  };

  const formatValue = (attr, value) => {
    if (!value) return 'N/A';
    if (['dob', 'dateOfCoverage'].includes(attr)) {
      return new Date(value).toLocaleDateString('en-GB');
    }
    return Array.isArray(value) ? value.join(', ') : value;
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '470px' }}><strong>Field</strong></TableCell>
            <TableCell style={{ width: '450px' }} sx={{ color: 'red' }}><strong>Old Value</strong></TableCell>
            <TableCell sx={{ color: 'green' }}><strong>New Value</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sumInsuredData?.sumInsuredHeaderSorted?.map((head) => {
            const attr = head.sumInsuredHeader.attribute;
            if (!selectedHeads.includes(attr)) return null;
            const label = head.title || head.sumInsuredHeader.title;
            const oldVal = oldRow['animalList']?.[0]?.[attr];
            const newVal = newRow[attr];
            return (
              <TableRow key={attr}>
                <TableCell>{label}</TableCell>
                <TableCell sx={{ color: oldVal !== newVal ? 'red' : 'inherit' }}>{formatValue(attr, oldVal)}</TableCell>
                <TableCell>{renderEditableInput(attr, newVal)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default AnimalListItemVerticalEditable;
